/**
 * Projects at risk -- the only forward-looking screen in the product.
 *
 * Three things here are load-bearing and none is obvious from the layout:
 *
 * 1. The order is the SERVICE'S order, by dollars, and this page cannot change
 *    it. There are no sort controls on the ranked table for that reason: a
 *    percentage sort puts a 6700%-of-budget overrun on a 96-hour demand above
 *    a six-figure one, which is the exact failure the engine's ranking rule
 *    exists to prevent.
 * 2. All three buckets are reachable as tabs with their counts on the labels.
 *    `unranked` (over budget, no cost rate to price it) and `not_assessable`
 *    (neither risk could be evaluated) are the rows whose DATA needs fixing;
 *    a board that silently drops them makes the gap invisible.
 * 3. `hours_source_warning` and `hours_source_identity.flagged` are wiring
 *    alarms about the whole page, not details of any row, so they sit above
 *    everything. If the hours feeding this board are not the hours behind the
 *    cost, every dollar on it is wrong by an unknown amount.
 * 4. The fourth bucket -- demands whose budget is PER PERIOD -- carries no
 *    dollars and no lifetime overrun, and the page never gives it either. This
 *    board once read a 96 h MONTHLY allowance as a budget for the life of a
 *    four-year staff-augmentation demand and reported $360,517 against Yankee
 *    Alliance; the real finding was about 28 hours a month. Nothing here
 *    multiplies a monthly budget by elapsed months, totals a monthly series,
 *    or lets these rows near a dollars-at-risk figure.
 */
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { PageHeaderBar } from "@/components/layout/PageHeaderBar";
import { usePageHeader } from "@/components/layout/PageHeaderContext";
import { AggregateRiskBoard } from "@/components/profitability/AggregateRiskBoard";
import { CostBasisNote } from "@/components/profitability/CostBasisNote";
import {
  PerPeriodSourceCell,
  PerPeriodStatusBadge,
} from "@/components/profitability/PerPeriodBudgetBadge";
import {
  ActionableMark,
  BillingTypeCell,
  RiskBasisBadge,
} from "@/components/profitability/RiskBasisBadge";
import { RiskDrillInDialog, type RiskFocus } from "@/components/profitability/RiskDrillInDialog";
import { DataTable, ROW_CLASS } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { InfoHint } from "@/components/shared/info-hint";
import { InlineBar } from "@/components/shared/inline-bar";
import { KpiTile } from "@/components/shared/kpi-tile";
import { StatusChip } from "@/components/shared/status-chip";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useClientSort } from "@/hooks/use-client-sort";
import { useMyAccess } from "@/hooks/use-my-access";
import { useRangeParam } from "@/hooks/use-range-param";
import { isAggregateRisk, useRisk } from "@/hooks/use-risk";
import { useTabParam } from "@/hooks/use-tab-param";
import { COLUMN_HELP } from "@/lib/column-help";
import { hours as formatHours, money, percent, rate } from "@/lib/format";
import type { RiskNotAssessableRow, RiskPerPeriodRow, RiskRow } from "@/lib/profitability-api";

/** Budget consumed as a burn bar; the label keeps the true figure past 100%. */
function BudgetBar({ pct }: { pct: number | null }) {
  if (pct === null || pct === undefined) return <>—</>;
  return <InlineBar mode="burn" width={72} value={pct / 100} label={percent(pct)} />;
}

/**
 * "unknown" from the engine means too few closed months to compare, not that
 * the burn is mysterious -- the two read very differently in a scanned column.
 */
const TREND_CHIP: Record<string, { tone: "bad" | "warn" | "good" | "neutral"; label: string }> = {
  accelerating: { tone: "bad", label: "Rising" },
  steady: { tone: "warn", label: "Flat" },
  decelerating: { tone: "good", label: "Easing" },
  unknown: { tone: "neutral", label: "Not enough history" },
};

function TrendChip({ trend, reason }: { trend: string; reason?: string | null }) {
  const known = TREND_CHIP[trend] ?? { tone: "neutral" as const, label: trend };
  return (
    <StatusChip tone={known.tone} title={reason ?? undefined}>
      {known.label}
    </StatusChip>
  );
}

const EMPTY_RANKED: RiskRow[] = [];
const EMPTY_UNASSESSABLE: RiskNotAssessableRow[] = [];
const EMPTY_PER_PERIOD: RiskPerPeriodRow[] = [];

/**
 * The wiring alarm.
 *
 * Deliberately the loudest thing on a page that is otherwise careful not to
 * shout: this is not "one project looks odd", it is "the hours column and the
 * cost column may be describing different work", and nobody should read a
 * dollar figure below it until it is resolved.
 */
function HoursSourceAlarm({
  warning,
  identity,
  suspectCount,
}: {
  warning: string | null;
  identity: { flagged: boolean; reason: string; match_pct: number | null; compared: number };
  suspectCount: number;
}) {
  const flagged = warning !== null || identity.flagged;
  if (!flagged) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4"
    >
      <AlertTriangle className="mt-0.5 size-5 shrink-0 text-destructive" aria-hidden />
      <div className="space-y-1 text-sm">
        <p className="font-semibold text-destructive">
          The hours behind this board may not be the hours behind the cost
        </p>
        {warning ? <p>{warning}</p> : null}
        {identity.flagged ? <p>{identity.reason}</p> : null}
        {suspectCount > 0 ? (
          <p className="text-xs">
            {suspectCount} demand{suspectCount === 1 ? "" : "s"} flagged individually.
          </p>
        ) : null}
        <p className="text-xs">
          Every dollar figure on this page is overrun hours priced at a rate derived from booked
          cost. If the two series disagree, so do the dollars.
        </p>
      </div>
    </div>
  );
}

/** The all-clear, stated rather than left as the absence of an alarm. */
function HoursSourceOk({
  identity,
}: {
  identity: { match_pct: number | null; compared: number; matching: number; reason: string };
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <ShieldCheck className="size-3.5 shrink-0" aria-hidden />
      Hours source checked
      <InfoHint
        text={`${identity.matching} of ${identity.compared} demands (${percent(identity.match_pct)}) match their costed burn closely. ${identity.reason}`}
        data-hours-source
      />
    </span>
  );
}

export function RiskPage() {
  const [range, setRange] = useRangeParam();
  const [tab, setTab] = useTabParam("ranked");
  const { data: response, isLoading, isError, error, refetch } = useRisk(range);
  const { data: myAccess } = useMyAccess();
  // An Aggregate caller gets a different, percentage-only board for their own
  // book; everything below `data` is the full board's shape.
  const aggregate = response && isAggregateRisk(response) ? response : null;
  const data = response && !isAggregateRisk(response) ? response : undefined;
  const [focusId, setFocusId] = useState<string | null>(null);

  const ranked = data?.ranked ?? EMPTY_RANKED;
  const unranked = data?.unranked ?? EMPTY_RANKED;
  const notAssessable = data?.not_assessable ?? EMPTY_UNASSESSABLE;
  const perPeriod = data?.per_period_budget ?? EMPTY_PER_PERIOD;

  const focus = useMemo<RiskFocus | null>(() => {
    if (!focusId) return null;
    return (
      ranked.find((r) => r.demand_id === focusId) ??
      unranked.find((r) => r.demand_id === focusId) ??
      notAssessable.find((r) => r.demand_id === focusId) ??
      perPeriod.find((r) => r.demand_id === focusId) ??
      null
    );
  }, [focusId, ranked, unranked, notAssessable, perPeriod]);

  const rangeInfo = response?.range;
  const asOf = response?.as_of ?? null;
  // Published before the error branch, so a failed load still shows the title
  // and the range that produced it rather than an unlabelled error.
  usePageHeader(
    () => (
      <PageHeaderBar
        title="Projects at risk"
        fields={[
          // The counts that used to sit here are the four Stat cards below,
          // verbatim. The date is not: "as of" is the last CLOSED period end,
          // never today, and a risk board read as current is read wrongly.
          asOf
            ? {
                key: "as-of",
                label: "As of",
                content: (
                  <span
                    className="text-sm font-medium"
                    title="The last closed period end, never today."
                  >
                    {asOf}
                  </span>
                ),
              }
            : null,
        ]}
        range={rangeInfo}
        rangeValue={range}
        onRangeChange={setRange}
      />
    ),
    [asOf, rangeInfo, range, setRange],
  );

  if (isError) {
    return (
      <div className="w-full space-y-4">
        <ErrorState message={(error as Error)?.message} onRetry={() => refetch()} />
      </div>
    );
  }

  if (aggregate) return <AggregateRiskBoard data={aggregate} />;
  // While loading, the full board's skeleton names "At risk" dollars and
  // "priced and ranked" -- not something to flash at an Aggregate user, or at
  // anyone before their level is known. A plain skeleton until then.
  if (!data && (myAccess?.isAggregate ?? true)) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {data ? (
        <HoursSourceAlarm
          warning={data.hours_source_warning}
          identity={data.hours_source_identity}
          suspectCount={data.suspect_hours_demand_ids.length}
        />
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Priced and ranked"
          value={String(ranked.length)}
          tone={ranked.length > 0 ? "bad" : "default"}
          help="Over budget or projected over, with a cost rate to price it"
        />
        <KpiTile
          label="Over budget, unpriced"
          value={String(unranked.length)}
          help="No blended cost rate on the demand, so the exposure has no dollars"
        />
        <KpiTile
          label="Not assessable"
          value={String(notAssessable.length)}
          help="No budget anywhere in the cascade, and no end date"
        />
        {/* The hint says "monthly" rather than leaving it to the tab body,
            because this card sits in a row of three whose counts all describe
            demands measured against a lifetime budget. Without it, a reader
            adds four numbers and believes they are the same kind of thing. */}
        <KpiTile
          label="Per-period budget"
          value={String(perPeriod.length)}
          help="Budget is a MONTHLY allowance -- no lifetime overrun, no dollars at risk"
        />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center gap-1">
          <TabsList>
            <TabsTrigger value="ranked" count={ranked.length}>
              Ranked
            </TabsTrigger>
            <TabsTrigger value="unpriced" count={unranked.length}>
              Over budget, unpriced
            </TabsTrigger>
            <TabsTrigger value="unassessable" count={notAssessable.length}>
              Not assessable
            </TabsTrigger>
            <TabsTrigger value="per-period" count={perPeriod.length}>
              Per-period budget
            </TabsTrigger>
          </TabsList>

          {data ? <InfoHint text={data.ranking_rule} data-ranking-rule /> : null}
        </div>

        <TabsContent value="ranked" className="space-y-3">
          {/* No sort controls anywhere in this table. See the file docstring. */}
          <Card flush>
            <DataTable
              flush
              dense
              loading={isLoading}
              headers={[
                { label: "Project", help: COLUMN_HELP.riskProject },
                { label: "What this is", help: COLUMN_HELP.riskBasis },
                // Next to the basis on purpose: the two COMPOSE. "Projected
                // overrun" on Fixed Price is margin walking out of the door;
                // the same words on Time & Material are billable hours and a
                // scope conversation. Neither column means much alone.
                { label: "Contract", help: COLUMN_HELP.riskContract },
                { label: "At risk", className: "text-right", help: COLUMN_HELP.dollarsAtRisk },
                { label: "Exposure", className: "text-right", help: COLUMN_HELP.exposureHours },
                { label: "Budget used", className: "text-right", help: COLUMN_HELP.budgetConsumed },
                { label: "Calendar", className: "text-right", help: COLUMN_HELP.calendarElapsed },
                { label: "Burn", className: "text-right", help: COLUMN_HELP.burnTrend },
              ]}
            >
              {ranked.map((row) => (
                <RiskRowCells
                  key={row.demand_id}
                  row={row}
                  onFocus={() => setFocusId(row.demand_id)}
                />
              ))}
            </DataTable>
          </Card>
          {!isLoading && ranked.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="Nothing priced at risk in this range"
              description="No delivery project is over its hours budget or projected to go over it."
            />
          ) : null}
        </TabsContent>

        <TabsContent value="unpriced" className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Over budget, and the exposure is real &mdash; but these demands booked no cost against
            their hours, so there is no blended rate to price it with. They are not safer than the
            ranked rows; they are unmeasured.
          </p>
          <Card flush>
            <DataTable
              flush
              dense
              loading={isLoading}
              headers={[
                { label: "Project", help: COLUMN_HELP.riskProject },
                { label: "What this is", help: COLUMN_HELP.riskBasis },
                { label: "Contract", help: COLUMN_HELP.riskContract },
                { label: "Exposure", className: "text-right", help: COLUMN_HELP.exposureHours },
                { label: "Budget used", className: "text-right", help: COLUMN_HELP.budgetConsumed },
                { label: "Calendar", className: "text-right", help: COLUMN_HELP.calendarElapsed },
              ]}
            >
              {unranked.map((row) => (
                <tr key={row.demand_id} className={ROW_CLASS}>
                  <td className="px-4 py-2.5 font-medium">
                    <button
                      type="button"
                      className="text-left hover:underline"
                      onClick={() => setFocusId(row.demand_id)}
                    >
                      {row.demand_name ?? row.demand_id}
                    </button>
                    <ActionableMark actionable={row.actionable} />
                    {row.account_name ? (
                      <span className="block text-xs font-normal text-muted-foreground">
                        {row.account_name}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5">
                    <RiskBasisBadge basis={row.dollars_at_risk_basis} />
                  </td>
                  <td className="px-4 py-2.5">
                    <BillingTypeCell billingType={row.billing_type} />
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {row.exposure_hours === null ? "—" : `${formatHours(row.exposure_hours)} h`}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    <BudgetBar pct={row.schedule.budget.consumed_pct} />
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {percent(row.schedule.schedule.elapsed_pct)}
                  </td>
                </tr>
              ))}
            </DataTable>
          </Card>
          {!isLoading && unranked.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="Every over-budget project could be priced"
              description="No demand is over budget without a cost rate to value the overrun."
            />
          ) : null}
        </TabsContent>

        <TabsContent value="unassessable" className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Neither risk could be evaluated on these: no budget on the demand, in guidance or in the
            plan, and nothing to compare a schedule against. They are not known to be safe &mdash;
            they are the rows whose data needs fixing first.
          </p>
          <Card flush>
            <DataTable
              flush
              dense
              loading={isLoading}
              headers={[
                { label: "Project", help: COLUMN_HELP.riskProject },
                { label: "Why it cannot be assessed" },
                { label: "Hours booked", className: "text-right", help: COLUMN_HELP.hours },
                { label: "Starts", className: "text-right" },
              ]}
            >
              {notAssessable.map((row) => (
                <tr key={row.demand_id} className={ROW_CLASS}>
                  <td className="px-4 py-2.5 font-medium">
                    <button
                      type="button"
                      className="text-left hover:underline"
                      onClick={() => setFocusId(row.demand_id)}
                    >
                      {row.demand_name ?? row.demand_id}
                    </button>
                    {row.account_name ? (
                      <span className="block text-xs font-normal text-muted-foreground">
                        {row.account_name}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{row.reason}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {row.schedule.budget.actual_hours === null
                      ? "—"
                      : `${formatHours(row.schedule.budget.actual_hours)} h`}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {row.schedule.schedule.start_date ?? "—"}
                  </td>
                </tr>
              ))}
            </DataTable>
          </Card>
          {!isLoading && notAssessable.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="Every delivery project could be assessed"
              description="Every demand in this range has a budget or an end date to measure against."
            />
          ) : null}
        </TabsContent>

        <TabsContent value="per-period" className="space-y-3">
          <PerPeriodTable rows={perPeriod} loading={isLoading} onFocus={setFocusId} />
        </TabsContent>
      </Tabs>

      {data ? (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-muted-foreground">
          <HoursSourceOk identity={data.hours_source_identity} />
          {data.assumptions.length > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              What these figures assume
              <InfoHint text={data.assumptions.join("\n")} data-assumptions />
            </span>
          ) : null}
        </div>
      ) : null}

      <CostBasisNote basis={data?.cost_basis} />

      <RiskDrillInDialog
        row={focus}
        range={range}
        onOpenChange={(open) => {
          if (!open) setFocusId(null);
        }}
      />
    </div>
  );
}

/**
 * A signed rate, written so the sign is unmissable.
 *
 * "+27.70 h/mo" and "27.70 h/mo" are the difference between over and under, and
 * this column is half negative -- a reader scanning it needs the sign to do the
 * work, not the position in the sort.
 */
function signedPerMonth(value: number | null): string {
  if (value === null) return "—";
  return `${value > 0 ? "+" : ""}${formatHours(value)} h/mo`;
}

/**
 * The per-period bucket.
 *
 * Every column here is a RATE or a count of months. There is deliberately no
 * budget-consumed percentage, no exposure, and no dollar column: on these
 * demands the budget is a monthly allowance, and the lifetime arithmetic that
 * would fill those columns is the bug this bucket was created to fix. The
 * engine's own sentence rides under each row, because on the ten rows that
 * refuse a verdict the sentence IS the finding -- it names which two readings
 * of the budget disagree.
 *
 * The default sort is the service's own order (most hours a month over budget
 * first). Unlike the ranked table above, the columns do sort: there is no
 * dollar ranking here for a percentage sort to invert, so the usual rule that
 * every column sorts applies.
 */
function PerPeriodTable({
  rows,
  loading,
  onFocus,
}: {
  rows: RiskPerPeriodRow[];
  loading: boolean;
  onFocus: (demandId: string) => void;
}) {
  const sorted = useClientSort<RiskPerPeriodRow>(
    rows,
    (row, key) => {
      switch (key) {
        case "demand_name":
          return row.demand_name ?? row.account_name;
        case "billing_type":
          return row.billing_type;
        case "budget_source":
          return row.budget_source;
        case "status":
          return row.monthly.status;
        case "budget_hours_per_period":
          return row.monthly.budget_hours_per_period;
        case "mean_actual_hours_per_month":
          return row.monthly.mean_actual_hours_per_month;
        case "vs_period_budget_hours_per_month":
          return row.monthly.vs_period_budget_hours_per_month;
        case "costed_months_over_budget":
          return row.monthly.costed_months_over_budget;
        case "hours_past_end":
          return row.hours_past_end;
        default:
          return null;
      }
    },
    { key: "vs_period_budget_hours_per_month", dir: "desc" },
  );

  return (
    <>
      <p className="text-sm text-muted-foreground">
        The hours budget on these demands is an allowance PER MONTH, not a number for the life of
        the project. They carry no lifetime overrun and no dollars at risk, and nothing on this tab
        multiplies a monthly budget by elapsed months &mdash; doing exactly that is what once put
        $360,517 against a demand whose real finding was 28 hours a month.
      </p>
      <Card flush>
        <DataTable
          flush
          dense
          loading={loading}
          sort={sorted.sort}
          sortDir={sorted.sortDir}
          onSort={sorted.toggleSort}
          headers={[
            { label: "Project", sortKey: "demand_name", help: COLUMN_HELP.riskProject },
            { label: "Verdict", sortKey: "status", help: COLUMN_HELP.perPeriodStatus },
            { label: "Budget read", sortKey: "budget_source", help: COLUMN_HELP.perPeriodSource },
            { label: "Contract", sortKey: "billing_type", help: COLUMN_HELP.riskContract },
            {
              label: "Monthly budget",
              sortKey: "budget_hours_per_period",
              className: "text-right",
              help: COLUMN_HELP.perPeriodBudget,
            },
            {
              label: "Mean a month",
              sortKey: "mean_actual_hours_per_month",
              className: "text-right",
              help: COLUMN_HELP.perPeriodMean,
            },
            {
              label: "Against budget",
              sortKey: "vs_period_budget_hours_per_month",
              className: "text-right",
              help: COLUMN_HELP.perPeriodVsBudget,
            },
            {
              label: "Months over",
              sortKey: "costed_months_over_budget",
              className: "text-right",
              help: COLUMN_HELP.perPeriodCostedMonths,
            },
            {
              label: "Past end",
              sortKey: "hours_past_end",
              className: "text-right",
              help: COLUMN_HELP.exposureHours,
            },
          ]}
        >
          {sorted.rows.map((row) => (
            <PerPeriodRowCells
              key={row.demand_id}
              row={row}
              onFocus={() => onFocus(row.demand_id)}
            />
          ))}
        </DataTable>
      </Card>
      {!loading && rows.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No demand in this range carries a per-period budget"
          description="Every budget in the range reads as a number for the life of the demand."
        />
      ) : null}
    </>
  );
}

/** One per-period demand: the figures, then the engine's sentence beneath them. */
function PerPeriodRowCells({ row, onFocus }: { row: RiskPerPeriodRow; onFocus: () => void }) {
  const monthly = row.monthly;
  const over = monthly.status === "over_period_budget";
  return (
    <>
      <tr className="border-b-0 hover:bg-muted/30">
        <td className="px-4 py-2.5 font-medium">
          <button type="button" className="text-left hover:underline" onClick={onFocus}>
            {row.demand_name ?? row.demand_id}
          </button>
          <ActionableMark actionable={row.actionable} />
          {row.account_name ? (
            <span className="block text-xs font-normal text-muted-foreground">
              {row.account_name}
            </span>
          ) : null}
        </td>
        <td className="px-4 py-2.5">
          <PerPeriodStatusBadge status={monthly.status} />
        </td>
        <td className="px-4 py-2.5">
          <PerPeriodSourceCell source={row.budget_source} />
        </td>
        <td className="px-4 py-2.5">
          <BillingTypeCell billingType={row.billing_type} />
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
          {monthly.budget_hours_per_period === null
            ? "—"
            : `${formatHours(monthly.budget_hours_per_period)} h`}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
          {monthly.mean_actual_hours_per_month === null
            ? "—"
            : `${formatHours(monthly.mean_actual_hours_per_month)} h`}
          {monthly.elapsed_months === null ? null : (
            <span className="block text-xs font-normal text-muted-foreground">
              over {monthly.elapsed_months} month{monthly.elapsed_months === 1 ? "" : "s"}
            </span>
          )}
        </td>
        {/* Weighted only where the verdict is "over". A refusal must not borrow
            the emphasis of a finding, and an under-budget rate is not news. */}
        <td
          className={`whitespace-nowrap px-4 py-2.5 text-right tabular-nums ${
            over ? "font-medium text-destructive" : ""
          }`}
        >
          {signedPerMonth(monthly.vs_period_budget_hours_per_month)}
          {monthly.vs_period_budget_pct === null ? null : (
            <span className="block text-xs font-normal text-muted-foreground">
              {percent(monthly.vs_period_budget_pct)}
            </span>
          )}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
          {monthly.costed_months_compared === 0
            ? "—"
            : `${monthly.costed_months_over_budget} of ${monthly.costed_months_compared}`}
        </td>
        <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
          {row.hours_past_end === null || row.hours_past_end === 0
            ? "—"
            : `${formatHours(row.hours_past_end)} h`}
        </td>
      </tr>
      {/* The sentence, in the row rather than behind the drill-in. On the ten
          rows that state no verdict it is the only thing that says WHY -- which
          two readings of the budget disagree and by how much -- and a badge
          reading "No verdict" with nothing beside it is indistinguishable from
          a value the page failed to load. */}
      <tr className="border-b border-border last:border-0">
        <td colSpan={9} className="px-4 pb-3 text-xs text-muted-foreground">
          {row.reason}
        </td>
      </tr>
    </>
  );
}

/** One ranked row. Split out only so the table body stays readable. */
function RiskRowCells({ row, onFocus }: { row: RiskRow; onFocus: () => void }) {
  return (
    <tr
      className={`${ROW_CLASS} ${
        // Muted, not hidden. The money is already spent on these, which is a
        // different thing from them not mattering -- 65 of 108 ranked rows are
        // in this state and hiding them would erase most of the board.
        row.actionable ? "" : "text-muted-foreground"
      }`}
    >
      <td className="px-4 py-2.5 font-medium">
        <button type="button" className="text-left hover:underline" onClick={onFocus}>
          {row.demand_name ?? row.demand_id}
        </button>
        <ActionableMark actionable={row.actionable} />
        {/* The client, under the demand name. A board of SOW numbers is a
            board nobody can act on -- "Yankee Alliance SOW#1 Staff Aug" names
            the paperwork, not the relationship somebody has to go and have. */}
        {row.account_name ? (
          <span className="block text-xs font-normal text-muted-foreground">
            {row.account_name}
          </span>
        ) : null}
      </td>
      <td className="px-4 py-2.5">
        <RiskBasisBadge basis={row.dollars_at_risk_basis} />
      </td>
      <td className="px-4 py-2.5">
        <BillingTypeCell billingType={row.billing_type} />
      </td>
      <td className="px-4 py-2.5 text-right">
        <span className="tabular-nums font-medium text-foreground">
          {row.dollars_at_risk === null ? "—" : money(row.dollars_at_risk)}
        </span>
        {row.cost_per_hour !== null ? (
          <span className="block text-xs text-muted-foreground">{rate(row.cost_per_hour)}</span>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
        {row.exposure_hours === null ? "—" : `${formatHours(row.exposure_hours)} h`}
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
        <BudgetBar pct={row.schedule.budget.consumed_pct} />
      </td>
      <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums">
        {percent(row.schedule.schedule.elapsed_pct)}
      </td>
      {/* "unknown" from the engine means too few closed months to compare, not
          that the burn is mysterious -- and the two read very differently in a
          column someone is scanning for trouble. */}
      <td className="px-4 py-2.5 text-right">
        <TrendChip trend={row.trend.trend} reason={row.trend.reason} />
      </td>
    </tr>
  );
}
